import { useDiscoveryStore } from "@/stores/discoveryStore";
import { HeadContent, Link, Outlet, createRootRoute, useRouter, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

import { useCustomerStore } from "@/stores/customerStore";
import { usePublicDiscoveryStore } from "@/stores/publicDiscoveryStore";
import { I18nProvider } from "../lib/i18n";
import { normalizeLanguage } from "@/domain/localization";
import { Toaster } from "../components/ui/sonner";
import { DiscoveryHostProvider } from "../components/DiscoveryHost";
import { BusinessOnboardingHostProvider } from "@/business-onboarding/BusinessOnboardingHost";
import { PublicPrivacyNotice } from "../components/PublicPrivacyNotice";
import { useInitialSplashReady } from "@/hooks/useInitialSplashReady";

function NotFoundComponent() {
  useInitialSplashReady();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  useInitialSplashReady();
  const router = useRouter();

  useEffect(() => {
    void import("@/data/errors")
      .then(({ logTechnicalError }) => logTechnicalError({ operation: "render route", error, category: "unexpected", source: "client" }))
      .catch((loggingError) => console.error("[Qotta route error logger]", loggingError));
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  const needsDestinationSignal = useRouterState({ select: (state) =>
    state.location.pathname === "/" || state.location.pathname === "/app" || state.location.pathname.startsWith("/app/") });
  // Home and workspace signal their own account/data readiness. Other routes use
  // the router's settled, committed content without changing their lifecycle.
  useInitialSplashReady(!needsDestinationSignal);
  const adminConsole = useRouterState({ select: (state) => state.location.pathname === "/admin" || state.location.pathname.startsWith("/admin/") });
  const params = useRouterState({ select: (state) => state.matches.at(-1)?.params });
  const business = useCustomerStore((state) =>
    params && "customerId" in params && params.customerId === state.customerId && state.status === "ready"
      ? state.settings?.profile : undefined,
  );
  const discoveryBusiness = useDiscoveryStore((state) =>
    params && "publicCustomerSlug" in params && state.status === "ready"
      ? state.model?.model.business : undefined,
  );
  // Resolve once for the active company, including standalone and embedded Discovery.
  const language = adminConsole ? "en" : normalizeLanguage((discoveryBusiness ?? business)?.primaryLanguage) ?? "en";
  const companyKey = adminConsole ? "admin" : params && "customerId" in params ? `owner:${params.customerId}`
    : params && "publicCustomerSlug" in params ? `discovery:${params.publicCustomerSlug}` : "public";
  return (
    <>
      <HeadContent />
      <I18nProvider language={language} companyKey={companyKey}>
        <DiscoveryHostProvider>
          <BusinessOnboardingHostProvider>
            <Outlet />
            <PublicPrivacyNotice />
            <Toaster position="top-center" />
          </BusinessOnboardingHostProvider>
        </DiscoveryHostProvider>
      </I18nProvider>
    </>
  );
}
